/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { CREATE_POLICY } from "../graphql";
import { VApplication } from "../../../application";

import { toHeaderCase } from "js-convert-case";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ICreatePolicyProps {
  beneficiaryFirstName?: string;
  beneficiaryLastName?: string;
  beneficiaryStreetAddress?: string;
  beneficiaryCity?: String;
  beneficiaryCountry?: string;
  beneficiaryTelephone?: string;
  beneficiaryEmail?: string;
  policyOption?: any;
  policyCoverage: string;
  policyPremium?: string;
  intendedPurpose?: any;
  termsData?: any;
  country?: string;
}

export interface ICreatePolicyMethods extends IPluginMethodMap {
  getCreatePolicyProps(args: ICreatePolicyProps): Promise<any>;
}

export class CreatePolicyService implements IAgentPlugin {
  readonly methods: ICreatePolicyMethods;

  constructor() {
    this.methods = {
      getCreatePolicyProps: this.getProps.bind(this),
    };
  }

  async getProps(args: ICreatePolicyProps) {
    console.log(`args in create policy`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.mutate({
          mutation: CREATE_POLICY,
          variables: {
            input: {
              benificiaryDetail: {
                firstName: args?.beneficiaryFirstName,
                lastName: args?.beneficiaryLastName,
                email: args?.beneficiaryEmail,
                mobileNo: args?.beneficiaryTelephone,
                address: {
                  addressLine1: args?.beneficiaryStreetAddress,
                  city: args?.beneficiaryCity,
                  country: args?.beneficiaryCountry,
                },
              },
              countryName: args?.country,
              coverage: parseFloat(args?.policyCoverage),
              premium: args?.policyOption?.premium?.raw,
              productDefination: args?.policyOption?.id,
              useOfProceeds: this.getUseOfProceeds(args?.intendedPurpose),
              termsOfUse: this.getTermsOfUse(args?.termsData),
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data?.purchasePolicy) {
          return {
            policyNumber: res?.data?.purchasePolicy?.policyNumber,
            email: res?.data?.purchasePolicy?.purchasedBy?.email,
            phoneNumber:
              res?.data?.purchasePolicy?.purchasedBy?.primaryContactNo,
            phoneVerified: res?.data?.purchasePolicy?.purchasedBy.phoneVerified,
            premium: res?.data?.purchasePolicy?.premium,
            discount: res?.data?.purchasePolicy?.discountedPremium,
          };
        }
      }
    } catch (err: any) {
      console.log(`Sign In user error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
    // return false
  }

  private getUseOfProceeds(args: any) {
    let _temp = [];
    for (const [key, value] of Object.entries(args)) {
      if (value) {
        _temp.push({
          selected: true,
          value: key,
        });
      } else {
        _temp.push({
          selected: false,
          value: key,
        });
      }
    }
    return _temp;
  }

  private getTermsOfUse(termsData: any) {
    let _temp = [];
    if (termsData?.politicallyExposed === "yes") {
      _temp.push({
        selected: true,
        value: "Politically Exposed",
      });
    }
    for (const [key, value] of Object.entries(termsData?.charged)) {
      if (value) {
        _temp.push({
          selected: true,
          value: key,
        });
      } else {
        _temp.push({
          selected: false,
          value: key,
        });
      }
    }
    return _temp;
  }
}
